import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
} from "@chakra-ui/react";
import { RiArrowDownSLine } from "@remixicon/react";

import { IcoClose } from "@/common/components/Icons";
import { UserName } from "@reflag/shared/userAPI";
import { OptionalKeys } from "@reflag/shared/utils/types";

import { MenuArrow } from "./MenuArrow";
import MenuDescription from "./MenuDescription";
import { PortalWrapper } from "./PortalWrapper";
import { UserDisplay } from "./UserDisplay";

interface MemberPickerProps {
  selectedMember: OptionalKeys<UserName, "name" | "email" | "avatarUrl"> | null;
  members: OptionalKeys<UserName, "name" | "email" | "avatarUrl">[];
  onSelect: (memberId: string) => void;
  onClear: () => void;
  placeholder?: string;
  description?: string;
  disabled?: boolean;
}

export function MemberPicker({
  selectedMember,
  members,
  onSelect,
  onClear,
  placeholder = "No owner",
  description = "Select a team member",
  disabled = false,
}: MemberPickerProps) {
  return (
    <Menu autoSelect={false} placement="bottom-start" closeOnSelect>
      <ButtonGroup
        alignItems="center"
        ml={-3}
        mr={selectedMember ? -2 : -3}
        my={-1}
        spacing={0}
      >
        <MenuButton
          as={Button}
          color={!selectedMember ? "dimmed" : undefined}
          flexGrow={1}
          isDisabled={disabled}
          rightIcon={
            <Box color="dimmed" fontSize="xl" mr={-1}>
              <RiArrowDownSLine size={16} />
            </Box>
          }
          size="sm"
          textAlign="left"
          variant="outlineOnHover"
          w="full"
        >
          {selectedMember ? (
            <UserDisplay user={selectedMember} onlyFirstName />
          ) : (
            <Text color="dimmed">{placeholder}</Text>
          )}
        </MenuButton>
        {selectedMember && (
          <IconButton
            aria-label="Clear selection"
            color="dimmed"
            icon={<IcoClose size="md" />}
            isDisabled={disabled}
            marginInlineStart={"-1px"}
            size="xs"
            variant="ghost"
            onClick={onClear}
          />
        )}
      </ButtonGroup>
      <PortalWrapper>
        <MenuList maxH="sm" maxW={280} overflow="auto">
          <MenuArrow />
          <MenuDescription>{description}</MenuDescription>
          <MenuDivider my={0} />
          <MenuOptionGroup
            type="radio"
            value={selectedMember?.id ?? ""}
            onChange={(value) => {
              const stringValue = Array.isArray(value) ? value[0] : value;
              onSelect(stringValue);
            }}
          >
            {members.map((member) => (
              <MenuItemOption key={member.id} value={member.id}>
                <UserDisplay user={member} onlyFirstName />
              </MenuItemOption>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </PortalWrapper>
    </Menu>
  );
}
